import { Component, inject, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/auth/services';
import { Snackbar, TranslationService } from 'src/app/services';
import { catchError, throwError } from 'rxjs';
import { SocialLoginButtonsComponent } from 'src/app/auth/components/social-login-buttons/social-login-buttons.component';

@Component({
  selector: 'app-register-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SocialLoginButtonsComponent],
  templateUrl: './register-dialog.component.html',
  styleUrls: ['./register-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RegisterDialogComponent {
  private _authService = inject(AuthService);
  private _dialogRef = inject(MatDialogRef<RegisterDialogComponent>);
  private _snackbar = inject(Snackbar);
  private _translationService = inject(TranslationService);

  passwordType: string = 'password';

  public registerForm = new FormGroup(
    {
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
      ]),
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
      confirmPassword: new FormControl('', Validators.required),
      checkBox: new FormControl(false, [Validators.requiredTrue]),
    },
    { validators: RegisterDialogComponent._checkPasswords },
  );

  get f() {
    return this.registerForm.controls;
  }

  get checkConditions(): boolean {
    return this.registerForm.get('checkBox')?.value || false;
  }

  protected register(): void {
    if (!this.registerForm.valid) return;

    const email = this.registerForm.get('email')?.value;
    const password = this.registerForm.get('password')?.value;

    this._authService
      .register(email, password)
      .pipe(
        catchError((err) => {
          const errors =
            typeof err?.error?.errors === 'object'
              ? (Object.values(err?.error?.errors)?.flat() as string[])
              : [];
          this._showError(errors);
          return throwError(() => new Error('HTTP request failed', { cause: err }));
        }),
      )
      .subscribe((res: any) => {
        if (res.status === 'OK') {
          this._snackbar.showSuccessSnackbar(
            this._translationService.instant('Registrado correctamente'),
            undefined,
            document.body,
          );
          this._dialogRef.close();
          this._authService.loginAndSetUser(email, password).subscribe();
          return;
        }
        this._showError();
      });
  }

  protected dismissDialog(): void {
    this._dialogRef.close();
  }

  protected showPassword(): void {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }

  protected getIconStatus(): string {
    return this.passwordType === 'password' ? 'eye-off' : 'eye';
  }

  protected focusin(evt: Event): void {
    evt.stopPropagation();
  }

  private _showError(errors: string[] = []): void {
    if (errors.length === 0) {
      this._snackbar.showErrorSnackbar(
        this._translationService.instant('Ha ocurrido un error al intentar registrarse'),
        undefined,
        document.body,
      );
      return;
    }
    const message = errors.map((e) => this._translationService.instant(e)).join('. ');
    this._snackbar.showErrorSnackbar(message, undefined, document.body);
  }

  private static _checkPasswords(group: FormGroup): { notSame: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { notSame: true };
  }
}
